import { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { dbSelect } from '../lib/api';
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  RefreshCw,
  X,
} from 'lucide-react';

const ADMIN_EMAIL = 'a.burkov@gmail.com';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

interface ApiKeyRecord {
  id: string;
  name: string;
  key_prefix: string;
  project_ids: string[];
  user_id: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
}

type ParsedProjectRef =
  | { kind: 'projectId'; value: string }
  | { kind: 'publicId'; value: string };

function parseProjectRef(url: string): ParsedProjectRef | null {
  const input = url.trim();
  if (!input) return null;

  const uuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
  const projectUrlMatch = input.match(new RegExp('/project/(' + uuidPattern + ')(?:$|[/?#])', 'i'));
  if (projectUrlMatch) return { kind: 'projectId', value: projectUrlMatch[1] };

  const rawUuidMatch = input.match(new RegExp('^(' + uuidPattern + ')$', 'i'));
  if (rawUuidMatch) return { kind: 'projectId', value: rawUuidMatch[1] };

  const publicUrlMatch = input.match(/\/p\/([^/?#]+)/i);
  if (publicUrlMatch) return { kind: 'publicId', value: publicUrlMatch[1] };

  const rawPublicIdMatch = input.match(/^[A-Za-z0-9]{8}$/);
  if (rawPublicIdMatch) return { kind: 'publicId', value: rawPublicIdMatch[0] };

  return null;
}

export function AdminApiKeysPage() {
  const { user, session, loading: authLoading } = useAuth();

  // Create key form
  const [projectInput, setProjectInput] = useState('');
  const [keyName, setKeyName] = useState('Default');
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(null);
  const [resolvedProjectName, setResolvedProjectName] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  // Keys list
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Newly created key (shown once)
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Project name cache
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});

  // Add project to existing key
  const [addProjectKeyId, setAddProjectKeyId] = useState<string | null>(null);
  const [addProjectInput, setAddProjectInput] = useState('');
  const [addingProject, setAddingProject] = useState(false);
  const [removingProject, setRemovingProject] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'API Keys - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  // Resolve project names for display
  const resolveProjectNames = useCallback(async (ids: string[]) => {
    if (!session || ids.length === 0) return;
    const unknown = ids.filter(id => !projectNames[id]);
    if (unknown.length === 0) return;

    const { data: projects } = await dbSelect<Array<{ id: string; name: string }>>('projects', {
      token: session.access_token,
      select: 'id,name',
      filters: { id: `in.(${unknown.join(',')})` },
    });
    if (projects) {
      const newNames: Record<string, string> = { ...projectNames };
      for (const p of projects) newNames[p.id] = p.name;
      setProjectNames(newNames);
    }
  }, [session, projectNames]);

  // Fetch all keys
  const fetchKeys = useCallback(async () => {
    if (!session) return;
    setKeysLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/miniapp-api-keys`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: ApiKeyRecord[] = await response.json();
      setKeys(data);

      // Collect all project IDs to resolve names
      const allPids = data.flatMap(k => k.project_ids);
      resolveProjectNames([...new Set(allPids)]);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setKeysLoading(false);
    }
  }, [session, resolveProjectNames]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) fetchKeys();
  }, [user, fetchKeys]);

  // Resolve project input to UUID
  const resolveProject = async (input: string, setter: (id: string, name: string) => void) => {
    if (!session || !input.trim()) return;
    const parsed = parseProjectRef(input);
    if (!parsed) {
      setResolveError('Could not parse. Enter a project URL, UUID, or public ID.');
      return;
    }

    let id = parsed.value;
    let name = '';

    if (parsed.kind === 'publicId') {
      const { data, error } = await dbSelect<{ id: string; name: string }>('projects', {
        token: session.access_token,
        select: 'id,name',
        filters: { public_id: 'eq.' + parsed.value },
        maybeSingle: true,
      });
      if (error || !data?.id) {
        setResolveError('No project found for: ' + parsed.value);
        return;
      }
      id = data.id;
      name = data.name;
    } else {
      const { data, error } = await dbSelect<{ id: string; name: string }>('projects', {
        token: session.access_token,
        select: 'id,name',
        filters: { id: 'eq.' + id },
        maybeSingle: true,
      });
      if (error || !data?.id) {
        setResolveError('Project not found: ' + id);
        return;
      }
      name = data.name;
    }

    setResolveError(null);
    setter(id, name);
  };

  const handleResolveForCreate = async () => {
    setResolving(true);
    setResolvedProjectId(null);
    setResolvedProjectName(null);
    await resolveProject(projectInput, (id, name) => {
      setResolvedProjectId(id);
      setResolvedProjectName(name);
    });
    setResolving(false);
  };

  // Create key
  const createKey = async () => {
    if (!session || !resolvedProjectId) return;
    setCreating(true);
    setNewKey(null);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/miniapp-api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: keyName || 'Default',
          projectIds: [resolvedProjectId],
        }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      setNewKey(result.key);
      setCopied(false);
      // Cache project name
      if (resolvedProjectName) {
        setProjectNames(prev => ({ ...prev, [resolvedProjectId]: resolvedProjectName }));
      }
      fetchKeys();
    } catch (err: any) {
      console.error('Failed to create key:', err);
      setResolveError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Add project to existing key
  const handleAddProject = async (keyId: string) => {
    if (!session || !addProjectInput.trim()) return;
    setAddingProject(true);
    setResolveError(null);

    await resolveProject(addProjectInput, async (projectId, projectName) => {
      try {
        const response = await fetch(`${SUPABASE_URL}/functions/v1/miniapp-api-keys/add-project`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ keyId, projectId }),
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        setProjectNames(prev => ({ ...prev, [projectId]: projectName }));
        setAddProjectInput('');
        setAddProjectKeyId(null);
        fetchKeys();
      } catch (err: any) {
        setResolveError(err.message);
      }
    });
    setAddingProject(false);
  };

  // Remove project from key
  const handleRemoveProject = async (keyId: string, projectId: string) => {
    if (!session) return;
    setRemovingProject(`${keyId}:${projectId}`);
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/miniapp-api-keys?id=${keyId}&projectId=${projectId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      fetchKeys();
    } catch (err) {
      console.error('Failed to remove project:', err);
    } finally {
      setRemovingProject(null);
    }
  };

  // Revoke key
  const revokeKey = async (keyId: string) => {
    if (!session) return;
    setRevokingId(keyId);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/miniapp-api-keys?id=${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      fetchKeys();
    } catch (err) {
      console.error('Failed to revoke key:', err);
    } finally {
      setRevokingId(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (authLoading) return <PageLoader />;
  if (!user || user.email !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/admin" className="btn btn-ghost btn-sm -ml-2">
            ← Back
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/10 text-violet-500">
            <Key className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-theme-text">
              API Keys
            </h1>
            <p className="text-theme-text-secondary">
              Create and manage API keys for external access to mini-app backends. Each key can be authorized for multiple projects.
            </p>
          </div>
        </div>

        {/* Create Key */}
        <div className="card p-6 mb-8">
          <h2 className="text-lg font-semibold text-theme-text mb-4">Create API Key</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-theme-text-muted mb-1">
                Initial project (URL, UUID, or public ID)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder="e.g. https://llambada.com/p/Bv3EUQCg/... or UUID"
                  value={projectInput}
                  onChange={(e) => {
                    setProjectInput(e.target.value);
                    setResolvedProjectId(null);
                    setResolvedProjectName(null);
                    setResolveError(null);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleResolveForCreate()}
                />
                <button
                  onClick={handleResolveForCreate}
                  disabled={resolving || !projectInput.trim()}
                  className="btn btn-secondary"
                >
                  {resolving ? <LoadingSpinner size="sm" /> : 'Resolve'}
                </button>
              </div>
              {resolveError && <p className="text-sm text-red-500 mt-1">{resolveError}</p>}
              {resolvedProjectId && resolvedProjectName && (
                <p className="text-sm text-green-600 mt-1">
                  Resolved: <strong>{resolvedProjectName}</strong> ({resolvedProjectId.slice(0, 8)}...)
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-muted mb-1">Key name</label>
              <input
                type="text"
                className="input w-full max-w-xs"
                placeholder="Default"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
              />
            </div>
            <button onClick={createKey} disabled={creating || !resolvedProjectId} className="btn btn-primary">
              {creating ? <LoadingSpinner size="sm" /> : <><Plus className="h-4 w-4" /> Create Key</>}
            </button>
          </div>

          {newKey && (
            <div className="mt-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                Key created. Copy it now — it won't be shown again.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-theme-surface rounded text-sm font-mono break-all">{newKey}</code>
                <button onClick={() => copyToClipboard(newKey)} className="btn btn-secondary btn-sm shrink-0">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Keys list */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-theme-text">All API Keys</h2>
              <span className="text-sm text-theme-text-muted">({keys.length})</span>
            </div>
            <button onClick={fetchKeys} disabled={keysLoading} className="btn btn-secondary btn-sm">
              <RefreshCw className={`h-4 w-4 ${keysLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {keysLoading ? (
            <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
          ) : keys.length === 0 ? (
            <div className="text-center py-8">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-theme-surface mb-4">
                <Key className="h-6 w-6 text-theme-text-muted" />
              </div>
              <p className="text-theme-text-muted">No API keys yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {keys.map((k) => (
                <div
                  key={k.id}
                  className={`p-4 rounded-lg border ${
                    k.is_active
                      ? 'bg-theme-surface/50 border-theme-border-muted'
                      : 'bg-theme-surface/20 border-theme-border-muted/50 opacity-60'
                  }`}
                >
                  {/* Header row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-theme-text">{k.name}</span>
                      <code className="text-xs text-theme-text-muted font-mono">{k.key_prefix}...</code>
                      {k.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-600 dark:text-green-400">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-600 dark:text-red-400">
                          Revoked
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-theme-text-muted">
                      <span>Created {new Date(k.created_at).toLocaleDateString()}</span>
                      {k.last_used_at && <span>Last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                      {k.is_active && (
                        <button
                          onClick={() => revokeKey(k.id)}
                          disabled={revokingId === k.id}
                          className="btn btn-ghost btn-sm text-red-500 hover:text-red-600"
                          title="Revoke key"
                        >
                          {revokingId === k.id ? <LoadingSpinner size="sm" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Projects */}
                  <div className="mt-2">
                    <span className="text-xs font-medium text-theme-text-muted">Projects:</span>
                    {k.project_ids.length === 0 ? (
                      <span className="text-xs text-theme-text-muted italic ml-2">All projects (unrestricted)</span>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {k.project_ids.map((pid) => (
                          <span
                            key={pid}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-500/10 text-xs"
                          >
                            <Link to={`/project/${pid}`} className="text-theme-primary hover:underline">
                              {projectNames[pid] || pid.slice(0, 8) + '...'}
                            </Link>
                            {k.is_active && (
                              <button
                                onClick={() => handleRemoveProject(k.id, pid)}
                                disabled={removingProject === `${k.id}:${pid}`}
                                className="text-theme-text-muted hover:text-red-500 ml-1"
                                title="Remove project"
                              >
                                {removingProject === `${k.id}:${pid}` ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Add project button / form */}
                    {k.is_active && (
                      <div className="mt-2">
                        {addProjectKeyId === k.id ? (
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              className="input text-sm py-1 flex-1 max-w-md"
                              placeholder="Project URL, UUID, or public ID"
                              value={addProjectInput}
                              onChange={(e) => setAddProjectInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddProject(k.id)}
                              autoFocus
                            />
                            <button
                              onClick={() => handleAddProject(k.id)}
                              disabled={addingProject || !addProjectInput.trim()}
                              className="btn btn-primary btn-sm"
                            >
                              {addingProject ? <LoadingSpinner size="sm" /> : 'Add'}
                            </button>
                            <button
                              onClick={() => { setAddProjectKeyId(null); setAddProjectInput(''); setResolveError(null); }}
                              className="btn btn-ghost btn-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setAddProjectKeyId(k.id); setAddProjectInput(''); setResolveError(null); }}
                            className="btn btn-ghost btn-sm text-xs mt-1"
                          >
                            <Plus className="h-3 w-3" /> Add project
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
